import { useState, useEffect } from "react";
import { Modal, Steps, message } from "antd";
import * as llamadasService from "../../../services/participantes/llamadasService";
import * as estadosService from "../../../services/participantes/estadosValidacionService";
import * as ubicacionesService from "../../../services/participantes/ubicacionesService";
import * as motivosService from "../../../services/participantes/motivosService";
import ModalHeader from "./components/ModalHeader";
import ModalFooter from "./components/ModalFooter";
import ParticipantInfoStep from "./steps/ParticipantInfoStep";
import CallResultStep from "./steps/CallResultStep";
import EligibilityStep from "./steps/EligibilityStep";
import ObservationStep from "./steps/ObservationStep";
import { getVisibleSteps, getAdjustedCurrentStep, mostrarElegibilidad, validarStep } from "./components/StepUtils";
import dayjs from 'dayjs';


export function AddCallModal({
  open,
  onClose,
  participant,
  callNumber,
  previousCalls,
  userInfo,
  motivosNoElegible
}) {
  const [estadoLlamadaId, setEstadoLlamadaId] = useState(null);
  const [subestadoLlamadaId, setSubestadoLlamadaId] = useState(null);
  const [observacionGeneral, setObservacionGeneral] = useState("");
  const [esElegible, setEsElegible] = useState(null);
  const [motivoNoElegibleId, setMotivoNoElegibleId] = useState(null);
  const [departamentoFueraTerritorio, setDepartamentoFueraTerritorio] = useState(null);
  const [municipioFueraTerritorio, setMunicipioFueraTerritorio] = useState(null);
  const [observacionNoElegible, setObservacionNoElegible] = useState("");
  const [estadosValidacion, setEstadosValidacion] = useState([]);
  const [subestadosDisponibles, setSubestadosDisponibles] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSubestados, setLoadingSubestados] = useState(false);
  const [errorCargaSubestados, setErrorCargaSubestados] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const estadoSeleccionado = estadosValidacion?.find(e => e.id === estadoLlamadaId) || null;
  const motivoSeleccionado = motivosNoElegible?.find(m => m.id === motivoNoElegibleId);
  const requiereUbicacion = motivoSeleccionado?.requiere_ubicacion === 1 || motivoSeleccionado?.requiere_ubicacion === true;
  const debeBloquearElegibilidad = estadoLlamadaId === 3 || (estadoLlamadaId === 1 && subestadoLlamadaId === 4);
  const esVerificado = estadoLlamadaId === 2;
  const mostrarEleg = mostrarElegibilidad(estadoLlamadaId, subestadoLlamadaId);
  const visibleSteps = getVisibleSteps(mostrarEleg);
  const adjustedCurrentStep = getAdjustedCurrentStep(currentStep, visibleSteps);
  const elegibilidadAutomatica = esVerificado || debeBloquearElegibilidad;
  const [motivosOtro, setMotivosOtro] = useState([]);
  const [motivoOtroSeleccionadoId, setMotivoOtroSeleccionadoId] = useState(null);
  const [motivoOtroOtro, setMotivoOtroOtro] = useState("");

  const [horaLlamada, setHoraLlamada] = useState(dayjs());


  useEffect(() => {
    if (open) {
      resetForm();
      cargarDatosIniciales();
      setCurrentStep(0);
    }
  }, [open]);

  useEffect(() => {
    if (estadoLlamadaId && estadoSeleccionado?.permite_subestados) {
      cargarSubestados(estadoLlamadaId);
    } else {
      setSubestadosDisponibles([]);
      setSubestadoLlamadaId(null);
      setErrorCargaSubestados(null);
    }
  }, [estadoLlamadaId]);

  useEffect(() => {
    if (esVerificado) {
      setEsElegible(true);
      setMotivoNoElegibleId(null);
      setObservacionNoElegible("");
      return;
    }
    if (debeBloquearElegibilidad) {
      setEsElegible(false);
    }
  }, [estadoLlamadaId, subestadoLlamadaId]);

  useEffect(() => {
    if (!mostrarEleg) {
      setEsElegible(null);
      setMotivoNoElegibleId(null);
      setDepartamentoFueraTerritorio(null);
      setMunicipioFueraTerritorio(null);
      setObservacionNoElegible("");
    }
  }, [mostrarEleg]);

  useEffect(() => {
    if (motivoSeleccionado?.codigo === 'OTRO') {
      // Buscar el motivo "Otro" para obtener su ID
      const motivoOtro = motivosNoElegible?.find(m => m.codigo === 'OTRO');
      if (motivoOtro?.id) {
        // Cargar los submotivos
        cargarMotivosOtro(motivoOtro.id);
      }
    } else {
      setMotivosOtro([]);
      setMotivoOtroSeleccionadoId(null);
      setMotivoOtroOtro("");
    }
  }, [motivoNoElegibleId]);


  // En la función cargarMotivosOtro
  const cargarMotivosOtro = async (motivoPadreId) => {
    try {
      // Pasar el número de llamada como parámetro
      const res = await motivosService.getMotivosOtroByPadreId(motivoPadreId, callNumber);
      setMotivosOtro(res.data || []);
    } catch (err) {
      console.error("Error cargando motivos 'Otro':", err);
      // Si falla, usar datos estáticos filtrados
      const motivosEstaticos = [
        { id: 1, codigo: 'INFO_NO_COMPROBABLE', nombre: 'Información no comprobable' },
        { id: 3, codigo: 'CUAL', nombre: '¿Cuál?' }
      ];

      // Solo agregar "Tres intentos" si es la tercera llamada
      if (callNumber === 3) {
        motivosEstaticos.splice(1, 0, {
          id: 2,
          codigo: 'TRES_INTENTOS_SIN_RESPUESTA',
          nombre: 'Tres intentos sin respuesta'
        });
      }

      setMotivosOtro(motivosEstaticos);
    }
  };
  const cargarDatosIniciales = async () => {
    try {
      const [estadosRes, deptosRes] = await Promise.all([
        estadosService.getEstadosValidacion(),
        ubicacionesService.getDepartamentos()
      ]);
      setEstadosValidacion(estadosRes.data || []);
      setDepartamentos(deptosRes.data || []);
    } catch (err) {
      console.error("Error cargando datos iniciales:", err);
      message.error("Error cargando datos iniciales");
    }
  };

  const cargarSubestados = async (estadoId) => {
    try {
      setLoadingSubestados(true);
      setErrorCargaSubestados(null);
      const res = await estadosService.getSubestadosValidacion(estadoId);
      setSubestadosDisponibles(res.data || []);
    } catch (err) {
      console.error("Error cargando subestados:", err);
      setErrorCargaSubestados("No se pudieron cargar los subestados");
      setSubestadosDisponibles([]);
    } finally {
      setLoadingSubestados(false);
    }
  };

  const handleDepartamentoChange = async (deptoId) => {
    setDepartamentoFueraTerritorio(deptoId);
    setMunicipioFueraTerritorio(null);

    if (deptoId) {
      try {
        const res = await ubicacionesService.getMunicipios(deptoId, 1);
        setMunicipios(res.data || []);
      } catch (err) {
        console.error("Error cargando municipios:", err);
        setMunicipios([]);
      }
    } else {
      setMunicipios([]);
    }
  };


  const resetForm = () => {
    setEstadoLlamadaId(null);
    setSubestadoLlamadaId(null);
    setObservacionGeneral("");
    setEsElegible(null);
    setMotivoNoElegibleId(null);
    setDepartamentoFueraTerritorio(null);
    setMunicipioFueraTerritorio(null);
    setObservacionNoElegible("");
    setMotivosOtro([]);
    setMotivoOtroSeleccionadoId(null);
    setMotivoOtroOtro("");
    setSubestadosDisponibles([]);
    setMunicipios([]);
    setErrorCargaSubestados(null);
    setLoading(false);
  };

  // En la función canProceedToNextStep
  const canProceedToNextStep = () => {
    return validarStep(
      currentStep,
      estadoLlamadaId,
      subestadoLlamadaId,
      estadoSeleccionado,
      mostrarEleg,
      esElegible,
      motivoNoElegibleId,
      requiereUbicacion,
      departamentoFueraTerritorio,
      municipioFueraTerritorio,
      observacionNoElegible,
      motivoSeleccionado,
      motivoOtroSeleccionadoId, // <-- Cambiado
      motivosOtro, // <-- Agregado
      motivoOtroOtro // <-- Cambiado
    );
  };

  const nextStep = () => {
    if (!canProceedToNextStep()) {
      message.error("Complete los campos obligatorios antes de continuar.");
      return;
    }
    const currentIndex = visibleSteps.findIndex(step => step.key === currentStep);
    if (currentIndex < visibleSteps.length - 1) {
      const nextStepKey = visibleSteps[currentIndex + 1].key;
      setCurrentStep(nextStepKey);
    }
  };

  const prevStep = () => {
    const currentIndex = visibleSteps.findIndex(step => step.key === currentStep);
    if (currentIndex > 0) {
      const prevStepKey = visibleSteps[currentIndex - 1].key;
      setCurrentStep(prevStepKey);
    }
  };

  const handleSave = async () => {
    if (!canProceedToNextStep()) {
      message.error("Debe completar todos los campos obligatorios.");
      return;
    }

    setLoading(true);

    // Validar si es motivo "Otro"
    const esMotivoOtro = motivoSeleccionado?.codigo === 'OTRO';

    // Si es motivo "Otro" pero no tiene ninguna opción seleccionada, mostrar error
    if (esMotivoOtro && !motivoOtroSeleccionadoId && esElegible === false) {
      message.error("Debe seleccionar una opción específica para 'Otro'.");
      setLoading(false);
      return;
    }

    // Si seleccionó "¿Cuál?" pero no escribió nada, mostrar error
    const motivoOtroSeleccionado = motivosOtro.find(m => m.id === motivoOtroSeleccionadoId);
    if (esMotivoOtro && motivoOtroSeleccionado?.codigo === 'CUAL' && (!motivoOtroOtro || motivoOtroOtro.trim() === '')) {
      message.error("Debe especificar el motivo cuando selecciona '¿Cuál?'.");
      setLoading(false);
      return;
    }

    const payload = {
      estadoLlamadaId,
      subestadoLlamadaId: subestadoLlamadaId || null,
      esElegible: esVerificado
        ? true
        : debeBloquearElegibilidad
          ? false
          : (mostrarEleg ? esElegible : null),
      observacionGeneral,
      motivoNoElegibleId:
        esElegible === false ? motivoNoElegibleId : null,
      departamentoFueraTerritorio:
        esElegible === false && requiereUbicacion
          ? departamentoFueraTerritorio
          : null,
      municipioFueraTerritorio:
        esElegible === false && requiereUbicacion
          ? municipioFueraTerritorio
          : null,
      observacionNoElegible:
        esElegible === false ? observacionNoElegible : null,
      // Nuevos campos simplificados
      motivoOtroInfoComprobable:
        esElegible === false && esMotivoOtro && motivoOtroSeleccionado?.codigo === 'INFO_NO_COMPROBABLE'
          ? true
          : null,
      motivoOtrTresIntentos:
        esElegible === false && esMotivoOtro && motivoOtroSeleccionado?.codigo === 'TRES_INTENTOS_SIN_RESPUESTA'
          ? true
          : null,
      motivoOtroCual:
        esElegible === false && esMotivoOtro && motivoOtroSeleccionado?.codigo === 'CUAL'
          ? "CUAL"
          : null,
      motivoOtroOtro:
        esElegible === false && esMotivoOtro && motivoOtroSeleccionado?.codigo === 'CUAL'
          ? motivoOtroOtro
          : null,
      fechaContacto: new Date().toISOString(),
      usuarioId: userInfo.usuarioId,
      hora_llamada: horaLlamada
        ? horaLlamada.format('HH:mm:ss')
        : null,
      ip: userInfo.ip
    };

    try {
      await llamadasService.createLlamada(
        participant.id,
        payload,
        userInfo.usuarioId,
        userInfo.ip
      );

      message.success({
        content: "Llamada registrada correctamente",
        duration: 2,
      });

      onClose(true, participant.id);

    } catch (err) {
      console.error("Error guardando llamada", err);
      let errorMsg = "No se pudo guardar la llamada.";
      if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      message.error({
        content: errorMsg,
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatOrdinal = (n) => n === 1 ? "Primera" : n === 2 ? "Segunda" : "Tercera";

  if (!participant) return null;

  return (
    <Modal
      open={open}
      onCancel={() => onClose(false)}
      footer={null}
      width="90%"
      destroyOnHidden={true}
      style={{ top: 15 }}
      maskClosable={false}
      title={
        <ModalHeader
          participant={participant}
          callNumber={callNumber}
          formatOrdinal={formatOrdinal}
        />
      }
    >

      <Steps
        responsive={false} // Evita que se apilen verticalmente
        size="small"       // Recomendado para móviles para ahorrar espacio
        current={visibleSteps.findIndex(step => step.key === adjustedCurrentStep)}
        style={{ marginBottom: 32 }}
        items={visibleSteps}
      />

      <div style={{ width: '100%' }}>
        {renderStepContent(adjustedCurrentStep)}
      </div>

      <ModalFooter
        currentStep={adjustedCurrentStep}
        visibleSteps={visibleSteps}
        onPrev={prevStep}
        onNext={nextStep}
        onSave={handleSave}
        canProceed={canProceedToNextStep()}
        loading={loading}
      />
    </Modal>
  );

  function renderStepContent(stepKey) {
    switch (stepKey) {
      case 0:
        return (
          <ParticipantInfoStep
            participant={participant}
            previousCalls={previousCalls}
          />
        );
      case 1:
        return (
          <CallResultStep
            callNumber={callNumber}
            estadoLlamadaId={estadoLlamadaId}
            subestadoLlamadaId={subestadoLlamadaId}
            setEstadoLlamadaId={setEstadoLlamadaId}
            setSubestadoLlamadaId={setSubestadoLlamadaId}
            estadosValidacion={estadosValidacion}
            subestadosDisponibles={subestadosDisponibles}
            loadingSubestados={loadingSubestados}
            errorCargaSubestados={errorCargaSubestados}
            horaLlamada={horaLlamada}
            setHoraLlamada={setHoraLlamada}
          />
        );
      case 2:
        return (
          <EligibilityStep
            estadoLlamadaId={estadoLlamadaId}
            esElegible={esElegible}
            setEsElegible={setEsElegible}
            bloquearElegibilidad={elegibilidadAutomatica}
            motivoNoElegibleId={motivoNoElegibleId}
            setMotivoNoElegibleId={setMotivoNoElegibleId}
            departamentoFueraTerritorio={departamentoFueraTerritorio}
            municipioFueraTerritorio={municipioFueraTerritorio}
            setDepartamentoFueraTerritorio={setDepartamentoFueraTerritorio}
            setMunicipioFueraTerritorio={setMunicipioFueraTerritorio}
            observacionNoElegible={observacionNoElegible}
            setObservacionNoElegible={setObservacionNoElegible}
            motivosNoElegible={motivosNoElegible}
            departamentos={departamentos}
            municipios={municipios}
            handleDepartamentoChange={handleDepartamentoChange}
            // Nuevos props simplificados
            motivosOtro={motivosOtro}
            motivoOtroSeleccionadoId={motivoOtroSeleccionadoId}
            setMotivoOtroSeleccionadoId={setMotivoOtroSeleccionadoId}
            motivoOtroOtro={motivoOtroOtro}
            setMotivoOtroOtro={setMotivoOtroOtro}
          />
        );
      case 3:
        return (
          <ObservationStep
            observacionGeneral={observacionGeneral}
            setObservacionGeneral={setObservacionGeneral}
          />
        );
      default:
        return null;
    }
  }
}